import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Spinner,
  Button,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import hash from 'object-hash';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight, faImageCircleArrowDown } from '@fortawesome/pro-regular-svg-icons';

import EventSelector from '../../../components/EventSelector/Modal';
import EventSelectorButton from '../../../components/EventSelector/Button';
import ColorsForm from './ColorsForm';
import UpdateButton from '../UpdateButton';
import RoundButton from '../../../components/RoundButton';
import { setColors } from './actions';

import { generateScreenshots, generatePreviewScreenshots, updateColors } from './apis';
import { useUpdateEvent } from '../hooks';
import { displaySuccessModal, displayErrorModal } from '../../../helpers/swal';
import imgPlaceholder from '../../../assets/images/image-placeholder.svg';

import env from '../../../config/env';
import AiTemplateColors from '../../AiTemplate/Colors';

import style from './style.module.scss';

const { origin } = window.location;
let screenshots = [
  { page: 'components', path: 'showcase/main', group: 'Main Colors' },
  { page: 'icons', path: 'showcase/icons', group: 'Main Colors' },
  { page: 'srsIcons', path: 'showcase/srsIcons', group: 'Main Colors' },
  { page: 'home', path: '', group: 'Onboarding' },
  { page: 'home-language', path: 'preview/language', group: 'Onboarding' },
  { page: 'home-user-info', path: '?showUserInfo=true', group: 'Onboarding' },
  { page: 'onboarding', path: 'onboarding', group: 'Onboarding' },
  { page: 'terms', path: 'terms', group: 'Onboarding' },
  { page: 'findme', path: 'findme', group: 'SRS' },
  { page: 'findme-section', path: 'findme/srs/section', group: 'SRS' },
  { page: 'findme-confirm', path: 'findme/confirm', group: 'SRS' },
  { page: 'trivia', path: 'livestream', group: 'Photo-Taking' },
  { page: 'next-in-line', path: 'livestream?queue=1', group: 'Photo-Taking' },
  { page: 'camera', path: 'camera', group: 'Photo-Taking' },
  { page: 'taking-photo', path: 'taking-photo', group: 'Photo-Taking' },
  { page: 'album', path: 'album', group: 'Album' },
  { page: 'snapshot', path: 'snapshot/1', group: 'Album' },
  { page: 'share', path: 'share/1', group: 'Share' },
  {
    page: 'snapshot-share',
    path: 'api/email/sendemailsnap/sample',
    server: true,
    group: 'Email',
  },
];

// Wrapper that picks the right implementation based on app type
const ColorsSwitch = (props) => {
  if (env.isSelfieCam()) return <AiTemplateColors />;
  return <ColorsOriginal {...props} />;
};

const ColorsOriginal = ({ colors, event }) => {
  const dispatch = useDispatch();
  const isEventUpdating = useUpdateEvent();
  const [selectedPage, setSelectedPage] = useState(screenshots[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [hashStr, setHashStr] = useState();
  const [pageHashes, setPageHashes] = useState({});
  const [pagePaths, setPagePaths] = useState({});
  const [pageLoadingStates, setPageLoadingStates] = useState({});
  const [pageTimeoutStates, setPageTimeoutStates] = useState({});
  const previewRef = useRef(false);

  useEffect(() => {
    if (event) {
      const eventHash = event.screenshotsHash;
      setHashStr(eventHash);

      // Initialize all pages with the event hash and saved path
      const initialHashes = {};
      const initialPaths = {};
      screenshots.forEach(({ page }) => {
        initialHashes[page] = eventHash;
        initialPaths[page] = 'saved'; // Start with saved path
      });
      setPageHashes(initialHashes);
      setPagePaths(initialPaths);

      if (event.availableLanguages.length <= 1) {
        screenshots = screenshots.filter(({ page }) => (page !== 'home-language'));
      }
    }
  }, [event]);

  const getImage = ({ page }, alt) => {
    if (!hashStr) {
      return (
        <div className={style.imgPlaceholder}>
          <img src={imgPlaceholder} alt={alt} />
        </div>
      );
    }

    // Use individual page hash if available, otherwise use the main hash
    const pageHash = pageHashes[page] || hashStr;
    // Use individual page path if available, otherwise use preview/saved based on previewRef
    const pagePath = pagePaths[page] || (previewRef.current ? 'preview' : 'saved');
    const url = `${origin}/screenshots/${pagePath}/${pageHash}/${page}.jpeg`;
    return <img src={url} alt={alt} />;
  };

  const handleExportColors = () => {
    if (!event) return;

    const data = { colors };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    saveAs(blob, `${event.slugDate}-colors.json`);
  };

  const isValidHex = (color) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);

  const handleImportColors = (evt) => {
    const fileInput = evt.target;
    const file = fileInput.files[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      displayErrorModal({ text: 'Invalid file type. Please upload a JSON file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        if (!jsonData.colors) {
          displayErrorModal({ text: 'No "colors" property found in the file.' });
          return;
        }

        const {
          theme = {},
          iconColor = {},
          emailSettings = {},
          slideshowColor,
        } = jsonData.colors;

        const validTheme = Object.fromEntries(
          Object.entries(theme).filter(([, color]) => isValidHex(color)),
        );
        const validIconColor = Object.fromEntries(
          Object.entries(iconColor).filter(([, color]) => isValidHex(color)),
        );
        const validEmailSettings = Object.fromEntries(
          Object.entries(emailSettings).filter(([, color]) => isValidHex(color)),
        );
        const validSlideshowColor = isValidHex(slideshowColor) ? slideshowColor : undefined;

        const hasValidColors = Object.keys(validTheme).length > 0
        || Object.keys(validIconColor).length > 0
        || Object.keys(validEmailSettings).length > 0
        || validSlideshowColor;

        if (!hasValidColors) {
          displayErrorModal({ text: 'No valid color settings found in the file.' });
          return;
        }

        dispatch(
          setColors({
            theme: { ...colors.theme, ...validTheme },
            iconColor: { ...colors.iconColor, ...validIconColor },
            emailSettings: { ...colors.emailSettings, ...validEmailSettings },
            slideshowColor: validSlideshowColor !== undefined
              ? validSlideshowColor
              : colors.slideshowColor,
          }),
        );
        displaySuccessModal({ text: 'Imported Colors Successfully.' });
      } catch (error) {
        displayErrorModal({
          text: error instanceof SyntaxError
            ? 'Invalid JSON format. Please check the file.'
            : 'Failed to Import Colors.',
        });
        console.error('Import error:', error);
      }
    };

    reader.onloadend = () => {
      fileInput.value = null;
    };

    reader.readAsText(file);
  };

  const handleRefreshScreenshot = async (pageName) => {
    if (pageLoadingStates[pageName] || pageTimeoutStates[pageName]) return;

    setPageLoadingStates((prev) => ({
      ...prev,
      [pageName]: true,
    }));

    setPageTimeoutStates((prev) => ({
      ...prev,
      [pageName]: true,
    }));

    setTimeout(() => {
      setPageTimeoutStates((prev) => ({
        ...prev,
        [pageName]: false,
      }));
    }, 3000);

    try {
      // Create a unique hash for this specific refresh operation
      const refreshHash = hash({
        ...colors,
        slugDate: event.slugDate,
        page: pageName,
        timestamp: Date.now(),
      });

      // Only generate the selected screenshot
      const screenshotsToGenerate = screenshots.filter(
        (screenshot) => screenshot.page === pageName,
      );

      await generatePreviewScreenshots({
        screenshots: screenshotsToGenerate,
        hash: refreshHash,
        slugDate: event.slugDate,
        theme: colors.theme,
        iconColor: colors.iconColor,
        emailSettings: colors.emailSettings,
      });

      previewRef.current = true;

      // Update only the hash and path for this specific page
      setPageHashes((prev) => {
        const updatedHashes = { ...prev };
        updatedHashes[pageName] = refreshHash;
        return updatedHashes;
      });

      setPagePaths((prev) => {
        const updatedPaths = { ...prev };
        updatedPaths[pageName] = 'preview';
        return updatedPaths;
      });
    } catch (err) {
      console.error('Error refreshing screenshot', err);
    } finally {
      setPageLoadingStates((prev) => ({
        ...prev,
        [pageName]: false,
      }));
    }
  };

  const requestScreenshots = useCallback(async ({ preview = true, eventIds }) => {
    const globalLoadingState = {};
    screenshots.forEach(({ page }) => {
      globalLoadingState[page] = true;
    });
    setPageLoadingStates(globalLoadingState);

    try {
      const newHash = hash({ ...colors, slugDate: event.slugDate });
      if (preview) {
        const { slugDate } = event;
        const { theme, iconColor, emailSettings } = colors;

        await generatePreviewScreenshots({
          screenshots,
          hash: newHash,
          slugDate,
          theme,
          iconColor,
          emailSettings,
        });
        previewRef.current = true;
        // Update both hashes when doing full preview
        setHashStr(newHash);
        // Reset all page hashes to the new hash
        const newPageHashes = {};
        const newPagePaths = {};
        screenshots.forEach(({ page }) => {
          newPageHashes[page] = newHash;
          newPagePaths[page] = 'preview'; // All screenshots go to preview path
        });
        setPageHashes(newPageHashes);
        setPagePaths(newPagePaths);
      } else {
        previewRef.current = false;
        await generateScreenshots({
          hash: newHash,
          screenshots,
          eventIds,
          slugDate: event.slugDate,
        });
        setHashStr(newHash);
        // Reset all page hashes to the new hash
        const newPageHashes = {};
        const newPagePaths = {};
        screenshots.forEach(({ page }) => {
          newPageHashes[page] = newHash;
          newPagePaths[page] = 'saved'; // All screenshots go to saved path
        });
        setPageHashes(newPageHashes);
        setPagePaths(newPagePaths);
      }
    } catch (err) {
      // TODO: we need to either have a UI message or repeat the request
      console.error('Error generating screenshots', err);
    } finally {
      setPageLoadingStates({});
    }
  }, [event, colors]);

  const updateEventHandler = useCallback(async (eventIds) => {
    await updateColors({ eventIds, colors });
    await requestScreenshots({ preview: false, eventIds });
  }, [event, colors]);

  const handleExportScreenshots = async () => {
    if (!event || !hashStr) {
      displayErrorModal({ text: 'No screenshots available to export.' });
      return;
    }

    setIsExporting(true);
    try {
      const zip = new JSZip();

      // Fetch all screenshots and add them to the zip
      const screenshotPromises = screenshots.map(async (screenshot) => {
        try {
          // Use the same logic as getImage to determine the correct path
          const pageHash = pageHashes[screenshot.page] || hashStr;
          const pagePath = pagePaths[screenshot.page] || (previewRef.current ? 'preview' : 'saved');
          const url = `${origin}/screenshots/${pagePath}/${pageHash}/${screenshot.page}.jpeg`;

          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`Failed to fetch ${screenshot.page}`);
          }

          const blob = await response.blob();
          return {
            name: `${screenshot.page}.jpeg`,
            data: blob,
          };
        } catch (error) {
          console.error(`Error fetching ${screenshot.page}:`, error);
          return null;
        }
      });

      const screenshotResults = await Promise.all(screenshotPromises);
      const validScreenshots = screenshotResults.filter((result) => result !== null);

      if (validScreenshots.length === 0) {
        displayErrorModal({ text: 'No screenshots could be downloaded.' });
        return;
      }

      // Add screenshots to zip
      validScreenshots.forEach(({ name, data }) => {
        zip.file(name, data);
      });

      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().replaceAll(/[:.]/g, '-').slice(0, -5);
      saveAs(zipBlob, `${event.slugDate}-screenshots-${timestamp}.zip`);

      displaySuccessModal({ text: `Successfully exported ${validScreenshots.length} screenshots.` });
    } catch (error) {
      displayErrorModal({ text: 'Failed to export screenshots.' });
      console.error('Error exporting screenshots:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const isPageLoading = (pageName) => pageLoadingStates[pageName] || false;

  const isPageDisabled = (pageName) => pageLoadingStates[pageName]
  || pageTimeoutStates[pageName] || false;

  return (
    <div className="d-flex flex-column flex-grow-1">
      <div className="pageHeader">
        <h4>UI Images</h4>
        <EventSelectorButton showFutureEvents />
        <div className="d-flex flex-row">
          <Button variant="outline-primary" size="sm" className={classNames('mr-2 rounded-pill', style.importBtn)}>
            Import Colors
            {' '}
            <input type="file" onChange={handleImportColors} />
          </Button>
          <Button variant="outline-primary" size="sm" onClick={handleExportColors} className="mr-2 rounded-pill">
            Export Colors
          </Button>
          <UpdateButton label="Update Event" updateHandler={updateEventHandler} />
        </div>
      </div>

      {isEventUpdating && (
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {!isEventUpdating && selectedPage && (
        <div className={style.gridContainer}>
          <div className={classNames('d-flex', 'flex-column', style.formArea)}>
            <div className="d-flex">
              <div className="flex-grow-1">
                <p className="mb-0">
                  Add your brand colours the Amplifi mobile web app.
                  Colors here will affect the entire experience.
                </p>
              </div>
              <div>
                <Button
                  className="small text-nowrap"
                  variant="outline-primary"
                  onClick={() => requestScreenshots({ preview: true })}
                >
                  Preview all
                </Button>
              </div>
            </div>
            <ColorsForm />
          </div>
          <div className={style.previewArea}>
            <div>
              <div className="d-flex justify-content-between align-items-center mt-3 mb-1 ml-3 mr-3">
                <div className="flex-grow-1 text-left">
                  <h5 className="ml-2">{selectedPage.page}</h5>
                </div>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Refresh current screenshot only</Tooltip>}
                >
                  <span className="mr-2">
                    <RoundButton
                      onClick={() => handleRefreshScreenshot(selectedPage.page)}
                      disabled={isPageDisabled(selectedPage.page)}
                      className={classNames(
                        style.refreshBtn,
                        { [style.refreshBtnDisabled]: isPageDisabled(selectedPage.page) },
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faRotateRight}
                        className={isPageLoading(selectedPage.page) ? style.spinning : ''}
                      />
                    </RoundButton>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Export Screenshots</Tooltip>}
                >
                  <span className="mr-2">
                    <RoundButton
                      onClick={() => handleExportScreenshots()}
                      disabled={isExporting || !hashStr}
                      className={classNames(
                        style.refreshBtn,
                        { [style.refreshBtnDisabled]: isExporting || !hashStr },
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faImageCircleArrowDown}
                      />
                    </RoundButton>
                  </span>
                </OverlayTrigger>
              </div>
              <div className={style.previewContainer}>
                <div>
                  {isPageLoading(selectedPage.page)
                    ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    )
                    : getImage(selectedPage, 'main preview')}
                </div>
              </div>
            </div>
          </div>
          <div className={style.carouselArea}>
            <div className={style.PagesCarousel}>
              {screenshots.map((item, index) => (
                // eslint-disable-next-line
                <div key={item.page} className={style.carouselItem} onClick={() => setSelectedPage(item)}>
                  <h6>
                    {`${index === 0 || screenshots[index - 1].group !== item.group ? item.group : ' '}`}
                  </h6>
                  <div
                    className={classNames(
                      style.imageContainer,
                      { [style.active]: item === selectedPage },
                    )}
                  >
                    {isPageLoading(item.page)
                      ? <Spinner animation="border" variant="primary" />
                      : getImage(item, item.page)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <EventSelector singleSelection showTemplate showFutureEvents />
    </div>
  );
};

export default connect(
  ({
    colors,
    events: { selectedEvents },
  }) => ({
    colors,
    event: selectedEvents[0],
  }),
)(ColorsSwitch);
